import React from 'react'
import styled from 'styled-components/macro'
import { darken } from 'polished'

import { RowBetween } from '../Row'
import { ChevronDown, Check } from 'react-feather'
import { Button as RebassButton, ButtonProps } from 'rebass/styled-components'
import useTheme from 'hooks/useTheme'

const Base = styled(RebassButton)<{
  padding?: string
  width?: string
  borderRadius?: string
  altDisabledStyle?: boolean
}>`
  padding: ${({ padding }) => (padding ? padding : '16px')};
  width: ${({ width }) => (width ? width : '100%')};
  font-weight: 500;
  text-align: center;
  border-radius: ${({ borderRadius }) => borderRadius && borderRadius};
  outline: none;
  border: 1px solid ${({ theme }) => theme.primary6};
  color: white;
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    cursor: auto;
    pointer-events: none;
  }

  will-change: transform;
  transition: transform 450ms ease;
  transform: perspective(1px) translateZ(0);

  &:hover {
    transform: scale(0.99);
    border: 1px solid #3b3e4f;
  }

  > * {
    user-select: none;
  }

  > a {
    text-decoration: none;
  }
`

export const ButtonPrimary = styled(Base)`
  color: #fff !important;
  border-radius: 16px !important;
  background-color: rgb(21, 242, 201, 0.1) !important;
  border: 1px solid rgb(21, 242, 201, 0.15) !important;
  &:focus {
    background: #15f2c9;
    border: 1px solid rgb(21, 242, 201, 0.15) !important;
    color: #fff !important;
  }
  &:hover {
    background: #15f2c9;
    border: 1px solid rgb(21, 242, 201, 0.15) !important;
    color: #fff !important;
  }
  &:active {
    // box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary1)};
    // background-color: ${({ theme }) => darken(0.1, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:disabled {
    background-color: ${({ theme, altDisabledStyle, disabled }) =>
      altDisabledStyle ? (disabled ? theme.primary1 : theme.primary1) : theme.secondary1_30};
    color: white;
    cursor: auto;
    box-shadow: none;
    border: 1px solid ${({ theme }) => theme.secondary1};
    outline: none;
    opacity: 0.4;
    opacity: ${({ altDisabledStyle }) => (altDisabledStyle ? '0.5' : '0.4')};
  }
`

export const ButtonLight = styled(Base)`
  background: #1a1f20;
  border: 1px solid ${({ theme }) => theme.primary6};
  color: ${({ theme }) => theme.primaryText2};
  &:focus {
    // background-color: ${({ theme }) => darken(0.05, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:hover {
    background: #1a1f20;
    border: 1px solid #2d3131;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:active {
    // box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary1)};
    // background-color: ${({ theme }) => darken(0.1, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  :disabled {
    opacity: 0.4;
    :hover {
      cursor: auto;
      background-color: ${({ theme }) => theme.primary6};
      box-shadow: none;
      border: 1px solid transparent;
      outline: none;
    }
  }
`

export const ButtonGray = styled(Base)`
  background: #1a1f20;
  border: 1px solid ${({ theme }) => theme.primary6};
  color: ${({ theme }) => theme.primaryText2};
  &:focus {
    // background-color: ${({ theme }) => darken(0.05, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:hover {
    background: #1a1f20;
    border: 1px solid #2d3131;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:active {
    // box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary1)};
    // background-color: ${({ theme }) => darken(0.1, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
`

export const ButtonSecondary = styled(Base)`
  background: #1a1f20;
  border: 1px solid ${({ theme }) => theme.primary6};
  color: ${({ theme }) => theme.primaryText2};
  &:focus {
    // background-color: ${({ theme }) => darken(0.05, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:hover {
    background: #1a1f20;
    border: 1px solid #2d3131;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:active {
    // box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary1)};
    // background-color: ${({ theme }) => darken(0.1, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
  a:hover {
    text-decoration: none;
  }
`

export const ButtonPink = styled(Base)`
  background: #1a1f20;
  border: 1px solid ${({ theme }) => theme.primary6};
  color: ${({ theme }) => theme.primaryText2};
  &:focus {
    // background-color: ${({ theme }) => darken(0.05, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:hover {
    background: #1a1f20;
    border: 1px solid #2d3131;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:active {
    // box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary1)};
    // background-color: ${({ theme }) => darken(0.1, theme.primary1)};
    border: 1px solid #3b3e4f;
    color: ${({ theme }) => theme.primaryText3};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.primary1};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonUNIGradient = styled(ButtonPrimary)`
  color: white;
  padding: 4px 8px;
  height: 36px;
  font-weight: 500;
  background-color: #15f2c9;
  background: #15f2c9;
  width: fit-content;
  position: relative;
  cursor: pointer;
  border: none;
  white-space: no-wrap;
  :hover {
    opacity: 0.8;
  }
  :active {
    opacity: 0.9;
  }
`

export const ButtonOutlined = styled(Base)`
  border: 1px solid #2d3131;
  box-shadow: 0 0 5px #15f2c9;
  background-color: transparent;
  color: ${({ theme }) => theme.text1};

  &:focus {
    box-shadow: 0 0 5px rgba(11, 13, 14, 0.85), 0 0 7px rgba(11, 13, 14, 0.85);
  }
  &:hover {
    box-shadow: 0 0 5px rgba(11, 13, 14, 0.85), 0 0 7px rgba(11, 13, 14, 0.85);
  }
  &:active {
    box-shadow: 0 0 5px #15f2c9;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonEmpty = styled(Base)`
  background-color: transparent;
  color: ${({ theme }) => theme.primary1};
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    text-decoration: underline;
  }
  &:hover {
    text-decoration: none;
  }
  &:active {
    text-decoration: none;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonText = styled(Base)`
  padding: 0;
  width: fit-content;
  background: none;
  text-decoration: none;
  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    text-decoration: underline;
  }
  &:hover {
    // text-decoration: underline;
    opacity: 0.9;
  }
  &:active {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonWhite = styled(Base)`
  border: 1px solid #edeef2;
  background-color: ${({ theme }) => theme.bg1};
  color: black;

  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    box-shadow: 0 0 0 1pt ${darken(0.05, '#edeef2')};
  }
  &:hover {
    box-shadow: 0 0 0 1pt ${darken(0.1, '#edeef2')};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${darken(0.1, '#edeef2')};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonConfirmedStyle = styled(Base)`
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text1};
  /* border: 1px solid ${({ theme }) => theme.green1}; */

  &:disabled {
    /* opacity: 50%; */
    background-color: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text2};
    cursor: auto;
  }
`

const ButtonErrorStyle = styled(Base)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.red1)};
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.red1)};
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
    box-shadow: none;
    background-color: ${({ theme }) => theme.red1};
    border: 1px solid ${({ theme }) => theme.red1};
  }
`

export function ButtonConfirmed({
  confirmed,
  altDisabledStyle,
  ...rest
}: { confirmed?: boolean; altDisabledStyle?: boolean } & ButtonProps) {
  if (confirmed) {
    return <ButtonConfirmedStyle {...rest} />
  } else {
    return <ButtonPrimary {...rest} altDisabledStyle={altDisabledStyle} />
  }
}

export function ButtonError({ error, ...rest }: { error?: boolean } & ButtonProps) {
  if (error) {
    return <ButtonErrorStyle {...rest} />
  } else {
    return <ButtonPrimary {...rest} />
  }
}

export function ButtonDropdown({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonPrimary {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonPrimary>
  )
}

export function ButtonDropdownGrey({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonGray {...rest} disabled={disabled} style={{ borderRadius: '20px' }}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonGray>
  )
}

export function ButtonDropdownLight({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonOutlined {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonOutlined>
  )
}

export function ButtonRadio({ active, ...rest }: { active?: boolean } & ButtonProps) {
  if (!active) {
    return <ButtonWhite {...rest} />
  } else {
    return <ButtonPrimary {...rest} />
  }
}

const ActiveOutlined = styled(ButtonOutlined)`
  border: 1px solid;
  border-color: ${({ theme }) => theme.primary1};
`

const Circle = styled.div`
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.primary1};
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckboxWrapper = styled.div`
  width: 30px;
  padding: 0 10px;
  position: absolute;
  top: 10px;
  right: 10px;
`

const ResponsiveCheck = styled(Check)`
  size: 13px;
`

export function ButtonRadioChecked({ active = false, children, ...rest }: { active?: boolean } & ButtonProps) {
  const theme = useTheme()

  if (!active) {
    return (
      <ButtonOutlined borderRadius="12px" padding="12px 8px" {...rest}>
        {<RowBetween>{children}</RowBetween>}
      </ButtonOutlined>
    )
  } else {
    return (
      <ActiveOutlined {...rest} padding="12px 8px" borderRadius="12px">
        {
          <RowBetween>
            {children}
            <CheckboxWrapper>
              <Circle>
                <ResponsiveCheck size={13} stroke={theme.white} />
              </Circle>
            </CheckboxWrapper>
          </RowBetween>
        }
      </ActiveOutlined>
    )
  }
}
